"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";

import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { CTACornerButton } from "@/shadcn/ui/corner-button";

import { PageAdShortSchema } from "@/sanity/pages/PageAdShortTemplate";

import checkmark from "@/public/ad-short/checkmark.svg";
import captionArrow from "@/public/conversion-lp-short/caption-arrow.svg";
import dots from "@/public/vsl-short/dots.svg";
import dotsM from "@/public/vsl-short/dots-m.svg";

// how much of the video loops as a muted preview before the user clicks play
const PREVIEW_LOOP_SECONDS = 10;

export default function HeroSection({
  data,
  className,
}: {
  data: PageAdShortSchema;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [started, setStarted] = useState(false);

  // Stable reference so a Sanity live-refresh (new object, same playbackId)
  // doesn't re-attach the HLS stream and reset the playhead to 0:00.
  const heroVideoSource = useMemo(
    () => data?.heroVideo,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.heroVideo?.muxVideo?.playbackId, data?.heroVideo?.embedUrl],
  );

  const hasVideo =
    heroVideoSource?.muxVideo?.playbackId || heroVideoSource?.embedUrl;

  useEffect(() => {
    // keep the inline preview muted so autoplay is allowed
    if (videoRef.current) videoRef.current.muted = true;
  }, []);

  // before the user clicks play, loop only the first seconds as a teaser
  useEffect(() => {
    const video = videoRef.current;
    if (!video || started) return;

    const loopPreview = () => {
      if (video.currentTime >= PREVIEW_LOOP_SECONDS) video.currentTime = 0;
    };
    video.addEventListener("timeupdate", loopPreview);
    return () => video.removeEventListener("timeupdate", loopPreview);
  }, [started]);

  // play from the start with audio + native controls when the user clicks
  const handleStart = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.muted = false;
    video.play();
    setIsMuted(false);
    setStarted(true);
  };

  const playButton = !started && (
    <button
      onClick={handleStart}
      className="absolute top-1/2 left-1/2 z-[10] rounded-full border -translate-x-1/2 -translate-y-1/2 hover:cursor-pointer border-white/20 p-[0.5rem]"
      aria-label={data?.heroPlayLabel || "Play Message"}
    >
      <span className="flex justify-center items-center rounded-full h-[7rem] gap-[1rem] bg-[#D9F9B9] px-[3rem] py-[1rem]">
        <svg
          className="h-[2rem] w-[2rem]"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M4 2.5v11l9-5.5-9-5.5z" fill="rgba(0,0,0,0.8)" />
        </svg>
        <span className="font-medium leading-none whitespace-nowrap font-geist text-[2.25rem] text-black/80">
          {data?.heroPlayLabel || "Play Message"}
        </span>
      </span>
    </button>
  );

  // caption overlay (bottom-left) + curved arrow pointing at the play button
  const videoOverlays = !started && (
    <>
      {data?.heroVideoCaption && (
        // outer wrapper holds the positioning — `[data-theme="new"] .richtext` forces `relative`
        <div className="absolute bottom-[2.5rem] left-[3rem] z-10">
          <div className="richtext richtext-start prose-p:mb-0 prose-p:font-funnel prose-p:text-[2.5rem] prose-p:leading-[1.1] prose-p:font-bold prose-p:text-white lg:prose-p:text-[4rem] prose-strong:font-bold prose-strong:text-white prose-em:font-ivy prose-em:font-light prose-em:text-[#EBFFD7]">
            <PortableText
              value={data.heroVideoCaption}
              components={richTextComponent}
            />
          </div>
        </div>
      )}
      <div
        className="absolute z-[2] h-full w-full"
        style={{
          background:
            "linear-gradient(40deg, #000 23.71%, rgba(0, 0, 0, 0.00) 50.33%)",
        }}
      />
      {data?.heroVideoCaption && (
        <Image
          src={captionArrow}
          alt=""
          aria-hidden
          className="pointer-events-none absolute bottom-[30%] left-[10%] z-10 w-[12.5%] lg:bottom-[22.4%] lg:left-[15%] lg:!w-[17%]"
        />
      )}
    </>
  );

  return (
    <section
      className={cn(
        "relative flex flex-col items-center px-[2rem] pt-[8rem] pb-[9rem] sm:px-[3rem] md:px-[5rem] lg:pb-[14rem]",
        className,
      )}
    >
      {/* horizontal divider under the header */}
      <DecorationLine className="top-0" orientation="horizontal" style="dark" />

      {/* vertical side lines flanking the content (near edges on mobile, matching Figma inset on desktop) */}
      <DecorationLine
        className="top-0 left-[1rem] lg:left-[8.85%]"
        orientation="vertical"
        style="dark"
      />
      <DecorationLine
        className="top-0 right-[1rem] lg:right-[8.85%]"
        orientation="vertical"
        style="dark"
      />

      <Image
        src={dots}
        alt=""
        className="hidden lg:block absolute top-0 left-1/2 -translate-x-1/2 !w-full lg:!w-[119.875rem] z-0 pointer-events-none"
      />
      <Image
        src={dotsM}
        alt=""
        className="lg:hidden absolute top-0 left-1/2 -translate-x-1/2 !w-full lg:!w-[119.875rem] z-0 pointer-events-none"
      />

      {/* eyebrow chip */}
      {data?.heroChip?.text && (
        <Chip
          className="mb-[2rem] [&_p]:uppercase [&_p]:font-geistmono [&_p]:text-[#202223] relative z-[1]"
          image={data?.heroChip?.icon}
          text={data?.heroChip?.text}
        />
      )}

      {/* headline */}
      {data?.heroTitle && (
        <div className="pixel-highlight richtext richtext-center w-[min(113.375rem,100%)] relative z-[1] prose-em:text-[5rem] sm:prose-em:text-[5.5rem] lg:prose-em:text-[7rem] prose-em:font-ivy prose-em:!px-0 lg:prose-em:!px-[.7rem] prose-headings:font-funnel prose-headings:font-medium prose-headings:text-black prose-headings:text-[4.5rem] sm:prose-headings:text-[5.5rem] lg:prose-headings:text-[7rem] prose-headings:leading-none prose-headings:text-balance">
          <PortableText value={data.heroTitle} components={richTextComponent} />
        </div>
      )}

      {/* subtext */}
      {data?.heroText && (
        <div className="richtext richtext-center mt-[2.25rem] w-[min(67.75rem,100%)] relative z-[1] prose-p:text-black/60 prose-p:text-[2.25rem] lg:prose-p:text-[2.25rem] prose-p:leading-[1.5]">
          <PortableText value={data.heroText} components={richTextComponent} />
        </div>
      )}

      {/* two columns: video left, features + CTA right */}
      <div className="relative z-[1] mt-[4.5rem] flex w-[min(150.125rem,100%)] flex-col items-start gap-[4rem] lg:flex-row ">
        {/* video — autoplays muted inline; click restarts from 0 with audio */}
        {hasVideo ? (
          <div className="relative w-full lg:w-[84.375rem] shrink-0 aspect-video overflow-clip [clip-path:polygon(0_0,calc(100%-5rem)_0,100%_5rem,100%_100%,0_100%)]">
            <HLSVideoPlayer
              ref={videoRef}
              source={heroVideoSource}
              autoPlay={!started}
              muted={isMuted}
              loop={!started}
              playsInline
              controls={started}
              poster={data?.heroVideoPlaceholder?.src}
              className="absolute left-0 top-0 !h-full !w-full object-cover"
            />
            {playButton}
            {videoOverlays}
          </div>
        ) : (
          data?.heroVideoPlaceholder?.src && (
            <div className="relative w-full lg:w-[84.375rem] shrink-0 aspect-video overflow-clip [clip-path:polygon(0_0,calc(100%-5rem)_0,100%_5rem,100%_100%,0_100%)]">
              <Image
                className="absolute left-0 top-0 !h-full !w-full object-cover"
                src={data.heroVideoPlaceholder}
                alt=""
              />
              {playButton}
              {videoOverlays}
            </div>
          )
        )}
        {data?.heroCTA && (
          <CTACornerButton className="lg:hidden" data={data.heroCTA} />
        )}

        {/* features + CTA */}
        <div className="flex flex-col w-full gap-[5rem] lg:w-[61.75rem]">
          {data?.heroFeatures?.length > 0 && (
            <div className="flex flex-col gap-[2.75rem]">
              {data.heroFeatures.map((feature, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <div className="w-full border-y border-t-[#202223]/[.15] border-b-white" />
                  )}
                  <div className="flex items-start gap-[2rem]">
                    <div className="flex justify-center items-center shrink-0 bg-[#0A1A1D] px-[1.125rem] py-[1rem]">
                      <Image
                        src={checkmark}
                        alt=""
                        className="!w-[3rem] !h-[3rem] object-contain"
                      />
                    </div>
                    <div className="flex flex-col gap-[1.5rem]">
                      {/* text-box-trim removes the half-leading so row heights match the Figma cap-height bounds */}
                      <p className="m-0 font-medium font-geist text-[2.25rem] leading-[1.5] text-[#0A1A1D] [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]">
                        {feature.title}
                      </p>
                      <p className="m-0 font-geist text-[2.25rem] leading-[1.5] text-black/60 [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]">
                        {feature.text}
                      </p>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}

          {data?.heroCTA && (
            <CTACornerButton className="self-start" data={data.heroCTA} />
          )}
        </div>
      </div>
    </section>
  );
}
